import { ConfirmModalComponent, SynDialogConfig } from '$synapse/core';
import { Group, GroupAssessment, Page } from '$synapse/models';
import { GroupService, StatsService } from '$synapse/services';
import { Component, effect, inject, input } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { SHARED_IMPORTS } from '../../shared-imports';
import { CertifiedAssessmentIconComponent } from '../certified-assessment-icon/certified-assessment-icon.component';
import { GroupAssessmentDTO } from '$synapse/dtos';

@Component({
  selector: 'syn-group-assessments-table',
  templateUrl: './group-assessments-table.component.html',
  styleUrls: ['./group-assessments-table.component.scss'],
  imports: [SHARED_IMPORTS, CertifiedAssessmentIconComponent],
})
export class GroupAssessmentsTableComponent {
  private readonly _groupService = inject(GroupService);
  private readonly _statsService = inject(StatsService);
  private readonly _route = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _modalDialog = inject(MatDialog);

  selectedGroupAssessments = new Array<GroupAssessment>();
  visible: boolean = false;
  allIsSelected: boolean;

  readonly groupAssessmentsPage = input<Page<GroupAssessment>>(undefined);
  readonly today: Date = new Date();
  readonly minDate: Date = new Date(
    this.today.getFullYear(),
    this.today.getMonth(),
    this.today.getDate() + 1
  );

  masterToggle() {
    this.allIsSelected = this.isAllSelected();
    if (this.isAllSelected()) {
      this.emptySelection();
    } else {
      this.emptySelection();
      this.groupAssessmentsPage().content.forEach((row) =>
        this._selectCandidate(row)
      );
    }
    this.visible = this.selectedGroupAssessments.length !== 0;
  }

  emptySelection() {
    this.selectedGroupAssessments = new Array<GroupAssessment>();
    this.visible = false;
  }

  boxChecked(groupAssessment: GroupAssessment) {
    this._selectCandidate(groupAssessment);
    this.visible = this.selectedGroupAssessments.length !== 0;
  }

  isAllSelected() {
    const numSelected = this.selectedGroupAssessments.length;
    const numRows = this.groupAssessmentsPage().content.length;
    return numSelected === numRows;
  }

  changeExpirationDate(
    groupId: string,
    groupAssessmentId: string,
    newExpirationDate: Date
  ) {
    const dialogRef = this._modalDialog.open(
      ConfirmModalComponent,
      new SynDialogConfig('send-date-change-confirmation')
    );
    let warn = `Attention ! Vous vous apprêtez à modifier la date d'expiration de l'évaluation.
    Cela enverra un e-mail à tous les candidats de ce groupe pour les prévenir de ce changement.`;

    const instance = dialogRef.componentInstance;
    instance.title = `Modification de la date d'expiration`;
    instance.message = 'Êtes-vous sûr de vouloir modifier cette date ?';
    instance.action = 'Modifier';
    instance.warn = warn;

    instance.confirmationEvent.subscribe(() => {
      this._groupService
        .updateGroupAssessmentExpirationDate(
          groupId,
          groupAssessmentId,
          newExpirationDate
        )
        .subscribe((groupAssessment: GroupAssessmentDTO) => {
          const updatedGroupAssessment =
            this.groupAssessmentsPage().content.find(
              (assessment) => assessment.id === groupAssessment.id
            );

          if (updatedGroupAssessment) {
            Object.assign(updatedGroupAssessment, groupAssessment, {
              expirationDate: new Date(groupAssessment.expirationDate),
            });
          }
        });
    });
  }

  deleteAssessment() {
    const modalConf = new SynDialogConfig('delete-assessment-confirmation');
    const dialogRef = this._modalDialog.open(ConfirmModalComponent, modalConf);
    const instance = dialogRef.componentInstance;
    instance.title = "Suppression d'évaluation";
    instance.message =
      'Êtes-vous sûr de vouloir supprimer cette(s) évaluation(s)?';
    instance.action = 'Supprimer';
    dialogRef.componentInstance.confirmationEvent.subscribe(() => {
      this._groupService
        .deleteGroupAssessments(
          this._route.snapshot.params['id'],
          this._getGroupAssessmentsIds()
        )
        .subscribe((isSuccess) => {
          if (isSuccess) {
            this.emptySelection();
            this._router.navigate(
              ['/groups/' + this._route.snapshot.params['id']],
              {
                queryParamsHandling: 'merge',
              }
            );
          }
        });
    });
  }

  async downloadCSV(
    groupAssessmentId: string,
    groupId: string,
    groupAssessmentName?: string
  ): Promise<void> {
    const groupObservable = this._groupService.getGroup(groupId);
    const group: Group = await firstValueFrom(groupObservable);
    this._statsService
      .downloadGroupAssessmentStatsReport(groupAssessmentId)
      .subscribe((blob: Blob): void => {
        this._createDownloadLink(blob, groupAssessmentName, group);
      });
  }

  private _createDownloadLink(
    blob: Blob,
    groupAssessmentName: string,
    group: Group
  ) {
    const link: HTMLAnchorElement = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    const todayDate: string = new Date().toISOString().slice(0, 10);
    link.download = `${groupAssessmentName || 'group_assessment'}_${
      group?.name || 'group'
    }_report_${todayDate}.csv`;
    link.click();
  }

  private _selectCandidate(groupAssessment: GroupAssessment) {
    if (this.selectedGroupAssessments.includes(groupAssessment)) {
      this._deleteInArray(groupAssessment);
    } else {
      this.selectedGroupAssessments.push(groupAssessment);
    }

    this.allIsSelected = this.isAllSelected();
  }

  private _deleteInArray(key: GroupAssessment) {
    const index = this.selectedGroupAssessments.indexOf(key);
    if (index > -1) {
      this.selectedGroupAssessments.splice(index, 1);
    }
  }

  private _getGroupAssessmentsIds(): number[] {
    let AssessmentsIds = [];
    this.selectedGroupAssessments.forEach((ass) => {
      AssessmentsIds.push(ass.id);
    });
    return AssessmentsIds;
  }
}
